import { useDroppable } from "@dnd-kit/core";
import React, { useCallback, useContext, useMemo, useState } from "react";
import type { TacticalSlot } from "@/generated/graphql";
import type { PitchMemberRemoveState } from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import type { WatchlistPitchDropData } from "@/features/scouting/components/molecules/WatchlistPitchDndContext";
import { PitchPlayerNavigationContext } from "@/features/scouting/contexts/PitchPlayerNavigationContext";
import PitchPositionNodeView, {
  PITCH_CARD_LINE_CAP,
  type Member,
  type PitchSquadPlayer,
  type ShowAllAction,
} from "./PitchPositionNodeView";

const playerNoun = (count: number): string =>
  count === 1 ? "player" : "players";

/**
 * The pop-out affordance: `Show all N`, N = every player at the slot (Figma
 * 281:2793 draws "Show all 5" under three names).
 * @param total - Every player at the slot, squad and targets
 * @param slot - The card's slot
 * @param onPress - Opens the pop-out
 * @returns The affordance
 */
const popoutAction = (
  total: number,
  slot: TacticalSlot,
  onPress: () => void
): ShowAllAction => ({
  label: `Show all ${total}`,
  accessibilityLabel: `Show all ${total} ${playerNoun(total)} at ${slot}`,
  isToggle: false,
  onPress,
});

/**
 * The inline affordance, only where no pop-out exists: `Show all N` /
 * `Show less`, N = every player at the slot.
 * @param total - Every player at the slot
 * @param expanded - Whether the card is expanded
 * @param slot - The card's slot
 * @param onPress - Toggles the expansion
 * @returns The affordance
 */
const toggleAction = (
  total: number,
  expanded: boolean,
  slot: TacticalSlot,
  onPress: () => void
): ShowAllAction => ({
  label: expanded ? "Show less" : `Show all ${total}`,
  accessibilityLabel: expanded
    ? `Show fewer players at ${slot}`
    : `Show all ${total} ${playerNoun(total)} at ${slot}`,
  isToggle: true,
  onPress,
});

/**
 * Owns the local disclosure state for one tactical position. SE-8486 (D1):
 * the card shows at most `PITCH_CARD_LINE_CAP` lines across squad + targets.
 * SE-8535: `Show all N` appears only when that hides someone, N = every
 * player at the slot, and it opens the pop-out whenever one is wired —
 * both pop-outs list ● and ○, so a squad-only slot opens it too.
 * @param props - Slot identity and ranked players.
 * @param props.slot - Tactical slot identity.
 * @param props.players - Players in watchlist order.
 * @param props.squadPlayers - Read-only current-squad players in this slot.
 * @param props.canAssign - Whether the slot accepts authorized assignment drops.
 * @param props.assignmentSlots - Every assignment option in the formation.
 * @param props.onAssign - Handles an explicit or dragged player assignment.
 * @param props.onOpenGroup - Opens the ranked pop-out for this slot.
 * @param props.onRemoveMember - Removes a member from the scenario (SE-8353); absent on the watchlist Pitch.
 * @param props.removeStateById - Per-member removal state (removing / failed / conflict), when Remove is offered.
 * @param props.removingMemberIds - Where each member's removal stands (SE-8353); idle when absent.
 * @param props.isWeakest - One of the three weakest positions (dashed border).
 * @param props.lineCap - Lines shown before Show all (2 on a compact card).
 */
const PitchPositionNodeContainer = (props: {
  slot: TacticalSlot;
  players: readonly Member[];
  squadPlayers?: readonly PitchSquadPlayer[];
  canAssign?: boolean;
  assignmentSlots?: readonly TacticalSlot[];
  onAssign?: (playerId: string, slot: TacticalSlot | null) => void;
  onOpenGroup?: (slot: TacticalSlot) => void;
  onRemoveMember?: (playerId: string) => void;
  removeStateById?: Readonly<Record<string, PitchMemberRemoveState>>;
  isWeakest?: boolean;
  /** Lines shown before Show all (SE-8535: 2 on a compact card). */
  lineCap?: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(value => !value), []);
  const { onOpenGroup, slot, players } = props;
  const openGroup = useCallback(() => onOpenGroup?.(slot), [onOpenGroup, slot]);
  const squadCount = props.squadPlayers?.length ?? 0;
  const total = players.length + squadCount;
  const lineCap = props.lineCap ?? PITCH_CARD_LINE_CAP;
  const showAllAction = useMemo<ShowAllAction | null>(() => {
    if (total <= lineCap) return null;
    return onOpenGroup
      ? popoutAction(total, slot, openGroup)
      : toggleAction(total, expanded, slot, toggle);
  }, [expanded, lineCap, onOpenGroup, openGroup, slot, toggle, total]);
  const dropData = useMemo<WatchlistPitchDropData>(
    () => ({ kind: "pitch-slot", tacticalSlot: props.slot }),
    [props.slot]
  );
  const { setNodeRef, isOver } = useDroppable({
    id: `watchlist-pitch-slot-${props.slot}`,
    data: dropData,
    disabled: !props.canAssign,
  });
  const viewProps = props;
  // SE-8605: the ● squad lines drawn here open the player page too, one
  // bound press per line so the View stays free of inline handlers.
  const openPlayer = useContext(PitchPlayerNavigationContext);
  const squadPlayers = props.squadPlayers;
  const openSquadPlayer = useMemo(
    () =>
      openPlayer
        ? Object.fromEntries(
            (squadPlayers ?? []).map(player => [
              player.id,
              () => openPlayer(player.id, player.displayName),
            ])
          )
        : undefined,
    [openPlayer, squadPlayers]
  );
  return (
    <PitchPositionNodeView
      {...viewProps}
      openSquadPlayer={openSquadPlayer}
      nodeRef={setNodeRef}
      isDropTarget={isOver}
      expanded={expanded}
      showAllAction={showAllAction}
      // PRD §4.6: the card itself opens the pop-out, whenever one is wired
      // and the slot holds someone.
      onOpenCard={onOpenGroup && total > 0 ? openGroup : undefined}
    />
  );
};
export default PitchPositionNodeContainer;
